import { useLoginState } from '@xing-com/crate-hooks-use-login-state';
import {
  isValidSalaryEstimate,
  type JobDetails,
} from '@xing-com/crate-jobs-helpers';
import { useBreakpoints } from '@xing-com/crate-jobs-hooks';

import type { ModuleVisibility } from '../types';

type ModulesVisibilityReturn = {
  visibleModules: ModuleVisibility;
};

const jobHasHeaderImage = (jobDetails: JobDetails): boolean => {
  // Check if the job description is of "TemplateData" type and contains a
  // header image URL
  if (
    jobDetails.description.__typename === 'TemplateData' &&
    !!jobDetails.description.headerImage
  )
    return true;

  // Check if the job description was compiled by XJM and contains a section
  // with the "header-image" class. False otherwise (which covers all the other
  // cases)
  return !!(
    jobDetails.description.__typename === 'HtmlDescription' &&
    jobDetails.description.content?.includes('<section class="header-image">')
  );
};

export const useModulesVisibility = (
  jobDetails: JobDetails,
  hasMatchingData: boolean
): ModulesVisibilityReturn => {
  const { isLoggedIn } = useLoginState();
  const { isMediaSmallOrTiny } = useBreakpoints();
  const { company, paid, redirectsToThirdPartyUrl, salary, summaryData } =
    jobDetails;

  const visibleModules: ModuleVisibility = {
    Header: true,
    AiSummary: !!summaryData && !redirectsToThirdPartyUrl, // NW1 already checks isLoggedIn and paid job to return summaryData
    MatchingSkills: hasMatchingData && !redirectsToThirdPartyUrl,
    MatchingBanner:
      paid &&
      !isLoggedIn &&
      !jobHasHeaderImage(jobDetails) &&
      !isMediaSmallOrTiny &&
      !redirectsToThirdPartyUrl,
    Description: true,
    Salary:
      !!salary && !redirectsToThirdPartyUrl && isValidSalaryEstimate(salary),
    Company: !!company?.id && !redirectsToThirdPartyUrl,
    Contacts: !!company?.id && isLoggedIn && !redirectsToThirdPartyUrl,
    // SearchAlerts: !redirectsToThirdPartyUrl,
    SearchAlerts: false, // Temporarily disabled until we can create more meaningful alerts with CS - https://new-work.atlassian.net/browse/JSR-5214
    SimilarJobs: !redirectsToThirdPartyUrl,
  };

  return {
    visibleModules,
  };
};
